import styled from '@emotion/styled';
import { useLingui } from '@lingui/react/macro';
import { CircleUserRound, Shield, Trophy } from 'lucide-react';
import { PlaceholderKey } from 'modules/manager/domain/placeholder/placeholder-keys';
import React from 'react';
import { STAT_COLUMNS } from './player-stats-scheme';
import { Hot, Logo, Poster, PosterHeader, SchemeTable, Subtitle, Title, Titles, useAllowsAny, useKeyText } from './scheme-parts';

/**
 * BIRTHDAY, both SYMBOLIC_TEAM_* types and the video goal plate: a card of one
 * player. On a symbolic team the card is the first place of the formation; the
 * birthday card has no team header, the round team no statistics, only the goal
 * plate has the minute and the score — `Hot` drops whatever the type does not
 * allow, so one sketch serves all of them.
 */
const PlayerCardScheme: React.FC = () => {
  const { t } = useLingui();
  const text = useKeyText();
  const hasStats = useAllowsAny(STAT_COLUMNS.map(({ key }) => key));
  return (
    <Poster>
      <PosterHeader>
        <Hot k={PlaceholderKey.TOURNAMENT_LOGO}>
          <Logo>
            <Trophy strokeWidth={1.5} />
          </Logo>
        </Hot>
        <Titles>
          <Hot k={PlaceholderKey.CHAMPIONSHIP_NAME}>
            <Title>{text(PlaceholderKey.CHAMPIONSHIP_NAME)}</Title>
          </Hot>
          <Hot k={PlaceholderKey.TOURNAMENT_NAME}>
            <Subtitle>{text(PlaceholderKey.TOURNAMENT_NAME)}</Subtitle>
          </Hot>
        </Titles>
      </PosterHeader>
      <Row>
        <Hot k={PlaceholderKey.SYMBOLIC_TEAM_NAME}>
          <b>{text(PlaceholderKey.SYMBOLIC_TEAM_NAME)}</b>
        </Hot>
        <Hot k={PlaceholderKey.SYMBOLIC_TEAM_FORMATION}>{text(PlaceholderKey.SYMBOLIC_TEAM_FORMATION)}</Hot>
        <Hot k={PlaceholderKey.ROUND_NAME}>{text(PlaceholderKey.ROUND_NAME)}</Hot>
      </Row>
      <Card>
        <Hot k={PlaceholderKey.PLAYER_PHOTO}>
          <Logo round>
            <CircleUserRound strokeWidth={1.5} />
          </Logo>
        </Hot>
        <Lines>
          <Hot k={PlaceholderKey.SYMBOLIC_SLOT_POSITION}>{text(PlaceholderKey.SYMBOLIC_SLOT_POSITION)}</Hot>
          <Hot k={PlaceholderKey.PLAYER_NAME}>
            <b>{text(PlaceholderKey.PLAYER_NAME)}</b>
          </Hot>
          <Row>
            <Hot k={PlaceholderKey.PLAYER_NUMBER}>{text(PlaceholderKey.PLAYER_NUMBER)}</Hot>
            <Hot k={PlaceholderKey.PLAYER_POSITION}>{text(PlaceholderKey.PLAYER_POSITION)}</Hot>
          </Row>
          <Row>
            <Hot k={PlaceholderKey.PLAYER_BIRTH_DATE}>{text(PlaceholderKey.PLAYER_BIRTH_DATE)}</Hot>
            <Hot k={PlaceholderKey.PLAYER_AGE}>{text(PlaceholderKey.PLAYER_AGE)}</Hot>
          </Row>
          <Row>
            <Hot k={PlaceholderKey.GOAL_MINUTE}>{text(PlaceholderKey.GOAL_MINUTE)}</Hot>
            <Hot k={PlaceholderKey.SCORE}>{text(PlaceholderKey.SCORE)}</Hot>
            <Hot k={PlaceholderKey.SCORE_TEAM}>{text(PlaceholderKey.SCORE_TEAM)}</Hot>
          </Row>
        </Lines>
      </Card>
      <Row>
        <Hot k={PlaceholderKey.TEAM_LOGO}>
          <Logo round>
            <Shield strokeWidth={1.5} />
          </Logo>
        </Hot>
        <Hot k={PlaceholderKey.TEAM_NAME}>{text(PlaceholderKey.TEAM_NAME)}</Hot>
        <Hot k={PlaceholderKey.TEAM_SHORT_NAME}>{text(PlaceholderKey.TEAM_SHORT_NAME)}</Hot>
      </Row>
      {hasStats && (
        <SchemeTable>
          <thead>
            <tr>
              {STAT_COLUMNS.map(({ key, heading }) => (
                <th key={key}>{t(heading)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {STAT_COLUMNS.map(({ key }) => (
                <td key={key}>
                  <Hot k={key}>{text(key)}</Hot>
                </td>
              ))}
            </tr>
          </tbody>
        </SchemeTable>
      )}
    </Poster>
  );
};

export default PlayerCardScheme;

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Lines = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  min-width: 0;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;

  &:empty {
    display: none;
  }
`;
